import type { ECharts } from "echarts/core";

export interface CrosshairBus {
    register: (chart: ECharts) => void;
    unregister: (chart: ECharts) => void;
}

type ZrPointerEvent = { offsetX: number; offsetY: number };

type MouseHandlers = {
    onMouseMove: (event: ZrPointerEvent) => void;
    onGlobalOut: () => void;
};

// A central place for charts to set up and tear down "crosshair" event handlers, so we can trigger tooltips
// across any other registered charts.
export function createCrosshairBus(): CrosshairBus {
    let broadcasting = false;
    const charts = new Set<ECharts>();
    const mouseHandlers = new Map<ECharts, MouseHandlers>();

    return {
        register: (sourceChart) => {
            const existingHandlers = mouseHandlers.get(sourceChart);
            if (existingHandlers) {
                const zr = sourceChart.getZr();
                zr.off("mousemove", existingHandlers.onMouseMove);
                zr.off("globalout", existingHandlers.onGlobalOut);
            }

            charts.add(sourceChart);

            const onMouseMove = (event: ZrPointerEvent) => {
                if (broadcasting) return;
                broadcasting = true;
                charts.forEach((peerChart) => {
                    if (peerChart === sourceChart) return;
                    const xFrom = sourceChart.convertFromPixel({ xAxisIndex: 0 }, event.offsetX);
                    const xTo = peerChart.convertToPixel({ xAxisIndex: 0 }, xFrom);
                    const heightRatio = peerChart.getHeight() / sourceChart.getHeight();
                    peerChart.dispatchAction({ type: "showTip", x: xTo, y: event.offsetY * heightRatio });
                });
                broadcasting = false;
            };

            // Hide all tooltips when mouse leaves any source chart
            // Specifically: don't need to bail if we're broadcasting; we would always want to hide all
            // tooltips when leaving a chart.
            const onGlobalOut = () => {
                broadcasting = true;
                charts.forEach((chart) => {
                    chart.dispatchAction({ type: "hideTip" });
                });
                broadcasting = false;
            };

            mouseHandlers.set(sourceChart, { onMouseMove, onGlobalOut });
            const zr = sourceChart.getZr();
            zr.on("mousemove", onMouseMove);
            zr.on("globalout", onGlobalOut);
        },
        unregister: (sourceChart) => {
            const handlers = mouseHandlers.get(sourceChart);
            if (handlers) {
                const zr = sourceChart.getZr();
                zr.off("mousemove", handlers.onMouseMove);
                zr.off("globalout", handlers.onGlobalOut);
                mouseHandlers.delete(sourceChart);
            }
            charts.delete(sourceChart);
        },
    };
}
